import React, { useRef } from 'react';
import { Helmet } from 'react-helmet';
import {
  Typography,
  Container,
  Box,
  Divider,
} from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer/Footer';
import InvestorBanner from '../components/InvestorBanner/InvestorBanner';

const data = [
  {
    id: 1,
    title: "Information We Collect",
    description: "We collect various types of information in connection with the services we provide, including:",
    points: [
      "Personal Information: Name, email address, phone number, postal address, and other contact details.",
      "Financial Information: Bank account details, investment preferences, and transaction history.",
      "Usage Information: Details about your interactions with our Site, including IP address, browser type, access times, and pages viewed.",
      "Cookies and Tracking Technologies: Information collected through cookies, web beacons, and similar technologies to enhance user experience"
    ]
  },
  {
    id: 2,
    title: "How We Use Your Information",
    description: "We use the information we collect to:",
    points: [
      "Provide, operate, and maintain our services.",
      "Improve, personalize, and expand our services",
      "Understand and analyze how you use our services.",
      "Develop new products, services, features, and functionality.",
      "Communicate with you, including for customer service, support, and to provide updates and information related to the Site.",
      "Process your transactions and manage your investments.",
      "Send you marketing and promotional communications.",
      "Detect and prevent fraud and other illegal activities.",
    ]
  }
];

const Privacy = () => {
  const footerRef = useRef(null);



  const handleEmailClick = () => {
    window.location.href = 'mailto:support@investofarms.com';
  };

  const SectionTitle = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "16px", md: "18px" }, 
        fontWeight: "600", 
        color: "#28947F",
        mb: 1.5
      }}
    >
      {children}
    </Typography>
  );

  const BodyText = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "13px", md: "14px" },
        lineHeight: 1.8,
        color: "#2c2c2c",
        mb: 2,
        textAlign: "justify"
      }}
    >
      {children}
    </Typography>
  );

  const BulletPoint = ({ children, bold }) => (
    <Box sx={{ display: "flex", gap: 1, mb: 1.5, pl: 1 }}>
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#28947F", fontWeight: "bold" }}>•</Typography>
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, lineHeight: 1.8, color: "#2c2c2c" }}>
        {bold && <strong style={{ color: "#28947F" }}>{bold}</strong>} {children}
      </Typography>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        flexDirection: "column", 
        background: `linear-gradient(90deg, rgba(209,230,226,1) 0%, rgba(234,238,236,1) 95%)`,
      }}
    >
      <Helmet>
        <title>Privacy Policy - InvestoFarms</title>
        <meta name="description" content="Discover our Privacy Policy ensuring data security in agriculture investing. Learn how we protect your information throughout your journey." />
      </Helmet>

      <Navbar  />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
          {/* Header */}
          <Typography 
            sx={{ 
              fontSize: { xs: "24px", md: "32px" }, 
              fontWeight: "700", 
              color: "#28947F", 
              textAlign: "center",
              mb: 4
            }}
          >
            Privacy Policy
          </Typography>

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <SectionTitle>Introduction</SectionTitle>
            <BodyText>
              Welcome to the InvestoFarms website (the "Site"), operated by InvestoFarms Partners Private Limited ("InvestoFarms Partners Private Limited," "InvestoFarms," "we," "us," or "our"). We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Site.
            </BodyText>
            <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
          </Box>

          {/* Dynamic Sections */}
          {data.map((item, index) => (
            <Box key={item.id} sx={{ mb: 4 }}>
              <SectionTitle>{index + 1}. {item.title}</SectionTitle>
              <BodyText>{item.description}</BodyText>
              <Box sx={{ mb: 2 }}>
                {item.points.map((point, idx) => (
                  <BulletPoint key={idx}>{point}</BulletPoint>
                ))}
              </Box>
              <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
            </Box>
          ))}

          {/* Sharing Your Information */}
          <Box sx={{ mb: 4 }}>
            <SectionTitle>3. Sharing Your Information</SectionTitle>
            <BodyText>
              We may share your information with third parties in the following circumstances:
            </BodyText>
            <Box sx={{ mb: 2 }}>
              <BulletPoint bold="Service Providers:">
                We may share your information with third-party vendors, consultants, and service providers who perform services on our behalf.
              </BulletPoint>
              <BulletPoint bold="Legal Requirements:">
                We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
              </BulletPoint>
              <BulletPoint bold="Business Transfers:">
                We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
              </BulletPoint>
              <BulletPoint bold="Protection of Rights:">
                We may disclose your information to protect and defend our rights or property, or the safety of our users or others.
              </BulletPoint>
            </Box>
            <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
          </Box>

          {/* Security of Your Information */}
          <Box sx={{ mb: 4 }}>
            <SectionTitle>4. Security of Your Information</SectionTitle>
            <BodyText>
              We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
            </BodyText>
            <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
          </Box>

          {/* Your Data Protection Rights */}
          <Box sx={{ mb: 4 }}>
            <SectionTitle>5. Your Data Protection Rights</SectionTitle>
            <BodyText>
              Depending on your location, you may have the following rights regarding your personal information:
            </BodyText>
            <Box sx={{ mb: 2 }}>
              <BulletPoint bold="Access:">
                The right to request copies of your personal information.
              </BulletPoint>
              <BulletPoint bold="Rectification:">
                The right to request that we correct any information you believe is inaccurate or incomplete.
              </BulletPoint>
              <BulletPoint bold="Erasure:">
                The right to request that we erase your personal information, under certain conditions.
              </BulletPoint>
              <BulletPoint bold="Restrict Processing:">
                The right to request that we restrict the processing of your personal information, under certain conditions.
              </BulletPoint>
              <BulletPoint bold="Object to Processing:">
                The right to object to our processing of your personal information, under certain conditions.
              </BulletPoint>
              <BulletPoint bold="Data Portability:">
                The right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.
              </BulletPoint>
            </Box>
            <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
          </Box>

          {/* Children's Privacy */}
          <Box sx={{ mb: 4 }}>
            <SectionTitle>6. Children's Privacy</SectionTitle>
            <BodyText>
              Our Site is not intended for children under the age of 18. We do not knowingly collect or solicit personal information from children under 18. If we learn that we have collected personal information from a child under age 18, we will delete that information as quickly as possible.
            </BodyText>
            <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.3)" }} />
          </Box>

          {/* Contact Us */}
          <Box sx={{ mt: 4, p: 3, backgroundColor: "rgba(255, 255, 255, 0.5)", borderRadius: 2, border: "1px solid rgba(40, 148, 127, 0.3)" }}>
            <SectionTitle>Contact Us</SectionTitle>
            <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#2c2c2c", mb: 1.5, lineHeight: 1.8 }}>
              If you have any questions or concerns about this Privacy Policy, please contact us at:
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#2c2c2c", mb: 1.5 }}>
              <strong>InvestoFarms</strong>
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#2c2c2c", mb: 2 }}>
              Email: <strong style={{ cursor: "pointer", color: "#28947F" }} onClick={handleEmailClick}>support@investofarms.com</strong>
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#2c2c2c", fontStyle: "italic" }}>
              By using our Site, you consent to our Privacy Policy.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={{ mt: "auto" }}>
        <InvestorBanner />
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default Privacy;